import { Sprite, type Texture } from "pixi.js";

export interface SpriteAnimatorOptions {
  fps?: number;
  loop?: boolean;
  /** Called when a non-loop animation finishes its last frame. */
  onComplete?: () => void;
}

/**
 * Manual frame animator. Drives a Pixi `Sprite.texture` from a frame array.
 *
 * Why not `AnimatedSprite`? Pixi's `AnimatedSprite` ties playback to the global
 * ticker which makes it awkward to drive from our fixed-step game loop and to
 * pause/scale per-entity. This class is dt-driven and trivially mockable.
 *
 *   const animator = new SpriteAnimator(frames, { fps: 8, loop: true });
 *   animator.attach(sprite);
 *   game.addUpdateSystem({ update: (dt) => animator.update(dt) });
 *
 * For per-entity animation that swaps clips on demand, see DirectionalAnimator
 * and AnimationStateMachine on top of this.
 */
export class SpriteAnimator {
  private frames: Texture[];
  private spf: number;
  private elapsed = 0;
  private frameIndex = 0;
  private finished = false;
  loop: boolean;
  paused = false;
  onComplete: (() => void) | undefined;

  constructor(frames: Texture[], opts: SpriteAnimatorOptions = {}) {
    if (frames.length === 0) throw new Error("SpriteAnimator requires at least one frame");
    this.frames = frames;
    this.spf = 1 / (opts.fps ?? 8);
    this.loop = opts.loop ?? true;
    this.onComplete = opts.onComplete;
  }

  setFrames(frames: Texture[], opts: SpriteAnimatorOptions = {}): void {
    if (frames.length === 0) throw new Error("setFrames requires at least one frame");
    this.frames = frames;
    if (opts.fps !== undefined) this.spf = 1 / opts.fps;
    if (opts.loop !== undefined) this.loop = opts.loop;
    this.onComplete = opts.onComplete ?? this.onComplete;
    this.elapsed = 0;
    this.frameIndex = 0;
    this.finished = false;
  }

  setFps(fps: number): void {
    this.spf = 1 / fps;
  }

  /** Bind to a sprite — initial frame is applied immediately. */
  attach(sprite: Sprite): void {
    sprite.texture = this.frames[this.frameIndex] ?? sprite.texture;
  }

  reset(): void {
    this.elapsed = 0;
    this.frameIndex = 0;
    this.finished = false;
  }

  /** Returns true if the animation finished (only meaningful when loop=false). */
  isFinished(): boolean {
    return this.finished;
  }

  /** Advance and return the current Texture for the caller to apply. */
  step(dt: number): Texture {
    if (!this.paused && !(this.finished && !this.loop)) {
      this.elapsed += dt;
      while (this.elapsed >= this.spf) {
        this.elapsed -= this.spf;
        this.frameIndex++;
        if (this.frameIndex >= this.frames.length) {
          if (this.loop) {
            this.frameIndex = 0;
          } else {
            this.frameIndex = this.frames.length - 1;
            if (!this.finished) {
              this.finished = true;
              this.onComplete?.();
            }
            break;
          }
        }
      }
    }
    return this.frames[this.frameIndex] ?? this.frames[0]!;
  }

  /** Convenience: step + write to sprite in one call. */
  update(dt: number, sprite?: Sprite): void {
    const tex = this.step(dt);
    if (sprite) sprite.texture = tex;
  }

  get currentFrame(): number {
    return this.frameIndex;
  }
}
